import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService.js';
import { useTranslation } from '../../i18n/useTranslation';

function monthLabel(t, month) {
  return t(`homePage.months.${month ?? 1}`);
}

function fmtNum(n, t, language) {
  if (language === 'en' && n >= 1000) {
    return (n / 1000).toFixed(n >= 10000 ? 0 : 1) + t('homePage.widgets.numberUnitThousand');
  }
  if (n >= 10000) return (n / 10000).toFixed(1) + t('homePage.widgets.numberUnitTenThousand');
  if (n >= 1000) return (n / 1000).toFixed(1) + t('homePage.widgets.numberUnitThousand');
  return String(n ?? 0);
}

function MiniBarChart({ data, t }) {
  if (!data || data.length === 0) return (
    <div style={{ height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-monotone-3)', fontSize: 11 }}>
      {t('homePage.widgets.noData')}
    </div>
  );
  const maxVal = Math.max(...data.map(d => d.totalViews ?? 0), 1);

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 56 }}>
      {data.map((d, i) => {
        const val = d.totalViews ?? 0;
        const h = Math.max((val / maxVal) * 46, val > 0 ? 3 : 1);
        const isLast = i === data.length - 1;
        const label = monthLabel(t, d.month);
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 2 }}>
            <div
              title={t('homePage.widgets.monthViewsTooltip', { month: label, count: val.toLocaleString() })}
              style={{
                width: '100%', height: h,
                background: 'var(--color-primary)',
                opacity: isLast ? 1 : 0.35,
                borderRadius: '2px 2px 0 0',
                transition: 'height 0.4s ease',
              }}
            />
            {(i === 0 || isLast) && (
              <span style={{ fontSize: 9, color: 'var(--color-monotone-4)', lineHeight: 1 }}>
                {label}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StatPill({ icon, label, value }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
      padding: '8px 6px',
      background: 'var(--color-monotone-1)',
      borderRadius: 10, flex: 1,
    }}>
      <span className="material-symbols-rounded" style={{ fontSize: 18, color: 'var(--color-primary)' }}>{icon}</span>
      <span style={{ fontSize: 15, fontWeight: 700 }}>{value}</span>
      <span style={{ fontSize: 10, color: 'var(--color-monotone-4)' }}>{label}</span>
    </div>
  );
}

function HomeWidgetPostStats() {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [summary, setSummary]   = useState(null);
  const [monthly, setMonthly]   = useState([]);
  const [topPosts, setTopPosts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const isLoggedIn = authService.isLoggedIn();

  const load = useCallback(async () => {
    if (!isLoggedIn) { setLoading(false); return; }
    const userData = authService.getUserData();
    const userId = userData?.userId ?? userData?.id;
    if (!userId) { setLoading(false); return; }

    setLoading(true);
    try {
      const [sumRes, monthlyRes, topRes] = await Promise.all([
        fetch(`/api/user-stats/posts/summary?userId=${userId}`, { credentials: 'include' }).then(r => r.json()),
        fetch(`/api/user-stats/posts/monthly?userId=${userId}&months=6`, { credentials: 'include' }).then(r => r.json()),
        fetch(`/api/user-stats/posts/top?userId=${userId}&limit=3`, { credentials: 'include' }).then(r => r.json()),
      ]);
      if (sumRes.success)  setSummary(sumRes.summary);
      if (monthlyRes.success) setMonthly(monthlyRes.monthly || []);
      if (topRes.success)  setTopPosts(topRes.posts || []);
    } catch {}
    setLoading(false);
  }, [isLoggedIn]);

  useEffect(() => { load(); }, [load]);

  const userId = authService.getUserData()?.userId ?? authService.getUserData()?.id;

  if (!isLoggedIn) return (
    <p style={{ color: 'var(--color-monotone-4)', fontSize: 13, textAlign: 'center', margin: 0 }}>
      {t('homePage.widgets.loginForPostStats')}
    </p>
  );

  if (loading) return (
    <div style={{ display: 'flex', gap: 8 }}>
      {[0, 1, 2].map(i => (
        <div key={i} className="skeleton-pulse" style={{ flex: 1, height: 72, borderRadius: 10 }} />
      ))}
    </div>
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 12, justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <StatPill icon="article"    label={t('homePage.widgets.posts')} value={fmtNum(summary?.totalPosts ?? 0, t, language)} />
        <StatPill icon="visibility" label={t('homePage.widgets.views')} value={fmtNum(summary?.totalViews ?? 0, t, language)} />
        <StatPill icon="favorite"   label={t('homePage.widgets.likes')} value={fmtNum(summary?.totalLikes ?? 0, t, language)} />
      </div>

      {monthly.length > 0 && (
        <div style={{ padding: '8px 0 0' }}>
          <span style={{ fontSize: 11, color: 'var(--color-monotone-4)', display: 'block', marginBottom: 6 }}>
            {t('homePage.widgets.monthlyViews')}
          </span>
          <MiniBarChart data={monthly} t={t} />
        </div>
      )}

      {topPosts.length > 0 && (
        <div>
          <span style={{ fontSize: 11, color: 'var(--color-monotone-4)', display: 'block', marginBottom: 4 }}>{t('homePage.widgets.popularPosts')}</span>
          {topPosts.map((p, i) => (
            <div
              key={p.id}
              onClick={() => navigate(`/post?id=${p.id}`)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '5px 0', borderBottom: '1px solid var(--color-monotone-1)',
                cursor: 'pointer',
              }}
            >
              <span style={{
                width: 16, height: 16, borderRadius: 8, flexShrink: 0,
                background: i < 3 ? 'var(--color-primary)' : 'var(--color-monotone-2)',
                color: i < 3 ? 'var(--color-on-primary)' : 'var(--color-monotone-5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, fontWeight: 700,
              }}>{i + 1}</span>
              <span style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</span>
              <span style={{ fontSize: 10, color: 'var(--color-monotone-4)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
                <span className="material-symbols-rounded" style={{ fontSize: 11 }}>visibility</span>
                {fmtNum(p.views, t, language)}
              </span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          className="btn-rtg btn-monotone-2"
          onClick={() => navigate(`/profile?userId=${userId}&item=analytics`)}
          style={{ fontSize: 12 }}
        >
          {t('homePage.widgets.viewDetails')}
          <span className="material-symbols-rounded" style={{ fontSize: 14 }}>arrow_forward</span>
        </button>
      </div>
    </div>
  );
}

export default HomeWidgetPostStats;
